import { Simulation, WEAPONS, clamp, validLevel, type Controls, type Difficulty, type Level, type WeaponId } from './core';
import { Renderer } from './renderer';
import { GameAudio } from './audio';
import { themeFor } from './environment';

const get = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const canvas = get<HTMLCanvasElement>('gameCanvas');
const surface = get<HTMLElement>('gameSurface');
const viewport = document.querySelector<HTMLElement>('.viewport')!;
const mapSelect = get<HTMLSelectElement>('mapSelect');
const difficultySelect = get<HTMLSelectElement>('difficultySelect');
const pauseDialog = get<HTMLDialogElement>('pauseDialog');
const upgradeDialog = get<HTMLDialogElement>('upgradeDialog');
const settingsDialog = get<HTMLDialogElement>('settingsDialog');
const audio = new GameAudio();
const renderer = new Renderer(canvas);
const difficultyNames: Record<Difficulty, string> = { easy: 'Découverte', normal: 'Standard', hard: 'Vétéran' };
type Preferences = { sensitivity: number; volume: number; audio: boolean; minimap: boolean; effects: boolean; low: boolean; invert: boolean };
let preferences: Preferences = { sensitivity: 1, volume: .2, audio: false, minimap: true, effects: !matchMedia('(prefers-reduced-motion: reduce)').matches, low: false, invert: false };
try {
  const saved = JSON.parse(localStorage.getItem('breach-settings') || '{}');
  for (const key of ['audio', 'minimap', 'effects', 'low', 'invert'] as const) if (typeof saved[key] === 'boolean') preferences[key] = saved[key];
  if (Number.isFinite(saved.sensitivity)) preferences.sensitivity = clamp(saved.sensitivity, .3, 2.5);
  if (Number.isFinite(saved.volume)) preferences.volume = clamp(saved.volume, 0, 1);
} catch { /* Defaults also work when storage is unavailable. */ }
let sim: Simulation;
let levels: Level[] = [];
let started = false, paused = false, needsRender = true, deathHandled = false, scoreSubmitted = false;
let lastTime = 0, hudTimer = 0, announcementTime = 0;
const keys = new Set<string>();
const touches = new Map<number, string>();
let mouseFire = false, aimPointer: number | null = null, aimX = 0;
const weapons: WeaponId[] = ['pistol', 'shotgun', 'gatling'];
const coarse = matchMedia('(pointer: coarse)').matches;
function text(id: string, value: string) { const node = get<HTMLElement>(id); if (node.textContent !== value) node.textContent = value; }
function persist() { try { localStorage.setItem('breach-settings', JSON.stringify(preferences)); } catch { /* In-memory settings remain usable. */ } }
function syncPreferences() {
  audio.enabled = preferences.audio; audio.volume = preferences.volume;
  text('soundBtn', `Son : ${audio.enabled ? 'oui' : 'non'}`); get('soundBtn').setAttribute('aria-pressed', String(audio.enabled));
  get<HTMLInputElement>('sensitivity').value = String(preferences.sensitivity);
  text('sensitivityValue', preferences.sensitivity.toFixed(1));
  get<HTMLInputElement>('volume').value = String(preferences.volume);
  for (const [id, key] of [['audioEnabled', 'audio'], ['minimapEnabled', 'minimap'], ['effectsEnabled', 'effects'], ['lowQuality', 'low'], ['invertStrafe', 'invert']] as const) get<HTMLInputElement>(id).checked = preferences[key];
  needsRender = true;
}
function clearInput() { keys.clear(); touches.clear(); mouseFire = false; aimPointer = null; }
function announce(message: string, seconds = 2) { text('announcement', message); announcementTime = seconds; }
function playing() { return started && !paused && !settingsDialog.open && sim.phase !== 'dead' && sim.phase !== 'upgrade'; }
function releasePointer() { if (document.pointerLockElement === canvas) document.exitPointerLock(); }
function lockPointer() {
  if (coarse || !canvas.requestPointerLock || document.pointerLockElement === canvas) return;
  try { const request = canvas.requestPointerLock(); if (request) void request.catch(() => announce('Capture souris indisponible · utilisez les flèches')); }
  catch { announce('Capture souris indisponible · utilisez les flèches'); }
}
function pause() {
  if (!started || sim.phase === 'dead' || sim.phase === 'upgrade' || paused) return;
  paused = true; clearInput(); releasePointer();
  if (!settingsDialog.open) pauseDialog.showModal();
  needsRender = true;
}
function resume() {
  if (pauseDialog.open) pauseDialog.close();
  if (!started || sim.phase === 'dead') return;
  paused = false; clearInput(); canvas.focus(); lockPointer(); audio.unlock(); needsRender = true;
}
function menu() {
  started = false; paused = false; clearInput(); releasePointer();
  for (const d of [pauseDialog, upgradeDialog, settingsDialog]) if (d.open) d.close();
  get('menuPanel').hidden = false; get('deathPanel').hidden = true;
  get('touchControls').hidden = true; viewport.classList.remove('running');
  get<HTMLButtonElement>('pauseBtn').disabled = true;
  text('objective', 'CHOISISSEZ VOTRE SECTEUR'); text('announcement', '');
  previewMap(); get('startBtn').focus(); needsRender = true;
}
function previewMap() {
  const level = levels.find(m => m.id === mapSelect.value); if (!level) return;
  const theme = themeFor(level);
  text('mapTheme', theme.name.toUpperCase()); text('mapSize', `${level.width} × ${level.height} · ${level.supplies?.length ?? 0} réserves`);
  text('mapDescription', level.description ?? 'Une zone ouverte pour prendre vos repères.');
  surface.style.setProperty('--sector-accent', theme.accent);
  const preview = get<HTMLCanvasElement>('mapPreview'), c = preview.getContext('2d')!;
  const scale = Math.min((preview.width - 12) / level.width, (preview.height - 12) / level.height);
  const ox = (preview.width - level.width * scale) / 2, oy = (preview.height - level.height * scale) / 2;
  c.fillStyle = '#0b1317'; c.fillRect(0, 0, preview.width, preview.height);
  for (let i = 0; i < level.data.length; i++) {
    c.fillStyle = level.data[i] === 3 ? '#edc471' : level.data[i] === 2 ? theme.accent : level.data[i] ? '#52606a' : '#182830';
    c.fillRect(ox + i % level.width * scale, oy + Math.floor(i / level.width) * scale, scale, scale);
  }
  for (const item of level.supplies ?? []) {
    c.fillStyle = item.kind === 'health' ? '#8af595' : item.kind === 'ammo' ? '#edc471' : '#76c3e7';
    c.fillRect(ox + item.x * scale - 1, oy + item.y * scale - 1, 3, 3);
  }
  c.fillStyle = '#edffe1'; c.beginPath(); c.arc(ox + level.playerStart.x * scale, oy + level.playerStart.y * scale, 2.4, 0, Math.PI * 2); c.fill();
  if (!started) { sim = new Simulation(level); sim.events = []; needsRender = true; hud(); }
}
function start() {
  const level = levels.find(m => m.id === mapSelect.value); if (!level) return;
  try { sim = new Simulation(level, difficultySelect.value as Difficulty); }
  catch { text('loadStatus', 'Cette carte ne permet pas de démarrer. Essayez un autre secteur.'); return; }
  started = true; paused = false; deathHandled = false; scoreSubmitted = false; clearInput();
  for (const d of [pauseDialog, upgradeDialog, settingsDialog]) if (d.open) d.close();
  get('menuPanel').hidden = true; get('deathPanel').hidden = true;
  get('touchControls').hidden = !coarse; viewport.classList.add('running');
  get<HTMLButtonElement>('pauseBtn').disabled = false;
  get<HTMLButtonElement>('saveScoreBtn').disabled = false; text('saveStatus', '');
  text('sector', `${themeFor(level).name.toUpperCase()} / ${difficultyNames[sim.difficulty].toUpperCase()}`);
  canvas.focus(); lockPointer(); audio.unlock(); needsRender = true; hud();
}
function hud() {
  text('healthValue', String(Math.ceil(sim.player.health))); text('armorValue', String(Math.ceil(sim.player.armor))); text('staminaValue', String(Math.floor(sim.player.stamina)));
  get('healthBar').style.width = `${sim.player.health}%`;
  get('healthBar').style.background = sim.player.health < 25 ? '#ff8668' : '#c6f484';
  text('weaponName', WEAPONS[sim.weapon].name.toUpperCase()); text('ammoValue', sim.weapon === 'pistol' ? '∞' : String(sim.ammo[sim.weapon]));
  text('weaponHint', sim.weapon === 'gatling' ? `CADENCE ${Math.round(sim.gatlingSpin * 100)} % · MAINTENIR LE TIR` : sim.weapon === 'shotgun' ? 'DISPERSION · REPOUSSE AU CONTACT' : 'PRÉCIS · MUNITIONS ILLIMITÉES');
  text('waveValue', String(sim.wave).padStart(2, '0')); text('killValue', String(sim.kills)); text('enemyValue', String(sim.enemies.filter(e => e.hp > 0).length));
  if (started) text('objective', sim.phase === 'dead' ? 'SIGNAL PERDU' : sim.phase === 'upgrade' ? 'AMÉLIORATION DISPONIBLE' : paused ? 'PARTIE EN PAUSE' : sim.phase === 'intermission' ? `PROCHAINE VAGUE · ${Math.ceil(sim.countdown)} S` : `V${sim.wave} / ${sim.waveLabel.toUpperCase()}`);
  document.querySelectorAll<HTMLButtonElement>('[data-weapon]').forEach(button => { const selected = button.dataset.weapon === sim.weapon; button.classList.toggle('selected', selected); button.setAttribute('aria-pressed', String(selected)); });
}

type Run = { name: string; kills: number; wave: number; time: number; difficulty: Difficulty; map: string };
let records: Run[] = [];
try {
  const data: unknown = JSON.parse(localStorage.getItem('breach-runs-v1') || '[]');
  if (Array.isArray(data)) records = data.filter((r): r is Run => !!r && typeof r.name === 'string' && typeof r.map === 'string' && ['easy', 'normal', 'hard'].includes(r.difficulty) && [r.kills, r.wave, r.time].every(v => Number.isFinite(v) && v >= 0)).slice(0, 5);
} catch { /* Empty journal. */ }
function renderRecords() {
  const list = get<HTMLOListElement>('localRecords'); list.replaceChildren();
  if (!records.length) { const row = document.createElement('li'); row.textContent = 'Votre première run commence ici.'; list.append(row); }
  for (const r of records) {
    const row = document.createElement('li'), detail = document.createElement('span');
    row.textContent = `${r.kills} frags · vague ${r.wave} · ${r.name.slice(0, 20)}`;
    detail.textContent = `${r.map.slice(0, 40)} / ${difficultyNames[r.difficulty]} / ${Math.floor(r.time / 60)} min ${Math.floor(r.time % 60)} s`;
    row.append(detail); list.append(row);
  }
}
let currentRun: Run | null = null;
function endRun() {
  if (deathHandled) return;
  deathHandled = true; clearInput(); releasePointer(); paused = false;
  get('deathPanel').hidden = false; get('touchControls').hidden = true; viewport.classList.remove('running');
  get<HTMLButtonElement>('pauseBtn').disabled = true;
  const accuracy = sim.shots ? Math.round(sim.hits / sim.shots * 100) : 0;
  text('deathSummary', `${sim.kills} frags · vague ${sim.wave} · ${Math.floor(sim.time / 60)} min ${Math.floor(sim.time % 60)} s · ${accuracy} % de tirs touchés`);
  const best = Math.max(0, ...records.filter(r => r.map === sim.level.name && r.difficulty === sim.difficulty).map(r => r.kills));
  currentRun = { name: 'Vous', kills: sim.kills, wave: sim.wave, time: sim.time, map: sim.level.name, difficulty: sim.difficulty };
  records.push(currentRun); records.sort((a, b) => b.kills - a.kills || b.wave - a.wave); records = records.slice(0, 5);
  let saved = true; try { localStorage.setItem('breach-runs-v1', JSON.stringify(records)); } catch { saved = false; }
  text('recordNotice', saved ? sim.kills > best ? 'Nouveau record personnel sur ce secteur et cette difficulté.' : 'Run enregistrée dans votre journal local.' : 'Stockage indisponible : record conservé pour cette visite.');
  renderRecords(); get('restartBtn').focus(); needsRender = true; hud();
}
async function sharedRecords() {
  try {
    const response = await fetch('/leaderboard.php?action=get', { signal: AbortSignal.timeout(4000) });
    if (!response.ok) throw Error('unavailable');
    const data = await response.json();
    if (!data.ok || !Array.isArray(data.leaderboard)) throw Error('invalid');
    const list = get('sharedRecords'); list.replaceChildren();
    for (const item of data.leaderboard.slice(0, 5)) {
      if (typeof item.name !== 'string' || !Number.isFinite(item.score)) continue;
      const li = document.createElement('li'); li.textContent = `${item.name.slice(0, 20)} · ${item.score} frags`; list.append(li);
    }
    text('sharedStatus', 'Classement historique, toutes cartes et difficultés confondues.');
  } catch { text('sharedStatus', 'Classement partagé indisponible. Vos records locaux restent accessibles.'); }
}

get('startForm').addEventListener('submit', event => { event.preventDefault(); start(); });
mapSelect.addEventListener('change', previewMap);
get('restartBtn').addEventListener('click', start); get('menuBtn').addEventListener('click', menu); get('pauseMenuBtn').addEventListener('click', menu);
get('pauseBtn').addEventListener('click', pause); get('resumeBtn').addEventListener('click', resume);
pauseDialog.addEventListener('cancel', event => { event.preventDefault(); resume(); });
upgradeDialog.addEventListener('cancel', event => event.preventDefault());
document.querySelectorAll<HTMLButtonElement>('[data-upgrade]').forEach(button => button.addEventListener('click', () => {
  sim.upgrade(button.dataset.upgrade as 'health' | 'damage' | 'speed'); upgradeDialog.close(); clearInput(); canvas.focus(); lockPointer(); needsRender = true;
}));
function settings() {
  pause(); if (pauseDialog.open) pauseDialog.close(); syncPreferences(); settingsDialog.showModal();
}
get('settingsBtn').addEventListener('click', settings); get('pauseSettingsBtn').addEventListener('click', settings);
function closeSettings() { settingsDialog.close(); if (paused && started && sim.phase !== 'dead') pauseDialog.showModal(); needsRender = true; }
get('settingsForm').addEventListener('submit', event => {
  event.preventDefault(); preferences.sensitivity = Number(get<HTMLInputElement>('sensitivity').value); preferences.volume = Number(get<HTMLInputElement>('volume').value);
  for (const [id, key] of [['audioEnabled', 'audio'], ['minimapEnabled', 'minimap'], ['effectsEnabled', 'effects'], ['lowQuality', 'low'], ['invertStrafe', 'invert']] as const) preferences[key] = get<HTMLInputElement>(id).checked;
  persist(); syncPreferences(); audio.unlock(); closeSettings();
});
get('sensitivity').addEventListener('input', () => text('sensitivityValue', Number(get<HTMLInputElement>('sensitivity').value).toFixed(1)));
settingsDialog.addEventListener('cancel', event => { event.preventDefault(); closeSettings(); });
get('soundBtn').addEventListener('click', () => { preferences.audio = !preferences.audio; syncPreferences(); audio.unlock(); persist(); });
get('fullscreenBtn').addEventListener('click', async () => {
  try { if (document.fullscreenElement) await document.exitFullscreen(); else if (surface.requestFullscreen) await surface.requestFullscreen(); else announce('Plein écran indisponible sur ce navigateur.'); }
  catch { announce('Plein écran indisponible sur ce navigateur.'); }
});
document.addEventListener('fullscreenchange', () => { get('fullscreenBtn').setAttribute('aria-label', document.fullscreenElement ? 'Quitter le plein écran' : 'Passer en plein écran'); needsRender = true; });
get('scoreForm').addEventListener('submit', async event => {
  event.preventDefault(); if (scoreSubmitted || !currentRun || !deathHandled) return;
  const name = get<HTMLInputElement>('playerName').value.trim().slice(0, 20); if (!name) return;
  const run = currentRun;
  run.name = name; try { localStorage.setItem('breach-runs-v1', JSON.stringify(records)); } catch { /* Local session still updated. */ }
  renderRecords(); scoreSubmitted = true; get<HTMLButtonElement>('saveScoreBtn').disabled = true; text('saveStatus', 'Enregistrement…');
  try {
    const response = await fetch('/leaderboard.php', { method: 'POST', body: new URLSearchParams({ action: 'submit', name, score: String(run.kills) }), signal: AbortSignal.timeout(5000) });
    if (!response.ok) throw Error('unavailable');
    const result = await response.json(); if (!result.ok) throw Error('failed');
    if (currentRun === run && deathHandled) text('saveStatus', result.accepted ? 'Score ajouté au classement partagé.' : 'Run conservée localement ; score hors du top 5 partagé.');
    void sharedRecords();
  } catch { if (currentRun === run && deathHandled) text('saveStatus', 'Classement partagé indisponible. Votre pseudo est conservé dans cette visite et, si possible, sur cet appareil.'); }
});
document.querySelectorAll<HTMLButtonElement>('[data-weapon]').forEach(button => button.addEventListener('click', () => { if (started) { sim.selectWeapon(button.dataset.weapon as WeaponId); hud(); canvas.focus(); } }));
function cycleWeapon(direction: number) { sim.selectWeapon(weapons[(weapons.indexOf(sim.weapon) + direction + 3) % 3]); hud(); }
canvas.addEventListener('wheel', event => { if (!playing()) return; event.preventDefault(); cycleWeapon(event.deltaY > 0 ? 1 : -1); }, { passive: false });
window.addEventListener('keydown', event => {
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement || event.target instanceof HTMLButtonElement || settingsDialog.open || upgradeDialog.open) return;
  if (event.code === 'Escape' || event.code === 'KeyP') { if (started) { event.preventDefault(); if (!paused) pause(); } return; }
  if (!playing()) return;
  if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.code)) {
    if (event.code === 'Tab') { pause(); return; }
    event.preventDefault();
  }
  keys.add(event.code);
  if (!event.repeat) {
    if (/^Digit[123]$/.test(event.code)) sim.selectWeapon(weapons[Number(event.code.slice(-1)) - 1]);
    if (event.code === 'KeyM') { preferences.minimap = !preferences.minimap; persist(); needsRender = true; }
  }
});
window.addEventListener('keyup', event => keys.delete(event.code));
window.addEventListener('blur', () => { clearInput(); pause(); });
document.addEventListener('visibilitychange', () => { if (document.hidden) { clearInput(); pause(); } });
document.addEventListener('pointerlockchange', () => { if (document.pointerLockElement !== canvas) { clearInput(); pause(); } });
document.addEventListener('pointerlockerror', () => announce('Capture souris indisponible · utilisez les flèches'));
document.addEventListener('mousemove', event => { if (playing() && document.pointerLockElement === canvas) sim.player.angle += event.movementX * .0026 * preferences.sensitivity; });
canvas.addEventListener('pointerdown', event => {
  if (!playing()) return;
  if (event.pointerType === 'touch') {
    if (event.clientX < canvas.getBoundingClientRect().left + canvas.clientWidth * .4) return;
    if (aimPointer !== null) return;
    aimPointer = event.pointerId; aimX = event.clientX; canvas.setPointerCapture(event.pointerId);
  } else if (event.button === 0) {
    if (document.pointerLockElement === canvas) mouseFire = true; else lockPointer();
  }
});
canvas.addEventListener('pointermove', event => { if (playing() && event.pointerId === aimPointer) { sim.player.angle += (event.clientX - aimX) * .007 * preferences.sensitivity; aimX = event.clientX; } });
function endPointer(event: PointerEvent) { touches.delete(event.pointerId); if (aimPointer === event.pointerId) aimPointer = null; if (event.pointerType !== 'touch') mouseFire = false; }
window.addEventListener('pointerup', endPointer); window.addEventListener('pointercancel', endPointer);
canvas.addEventListener('lostpointercapture', endPointer);
document.querySelectorAll<HTMLButtonElement>('[data-touch]').forEach(button => {
  button.addEventListener('pointerdown', event => { if (!playing()) return; event.preventDefault(); button.setPointerCapture(event.pointerId); touches.set(event.pointerId, button.dataset.touch!); });
  button.addEventListener('lostpointercapture', endPointer);
});
get('touchSwap').addEventListener('click', () => { if (playing()) cycleWeapon(1); });
canvas.addEventListener('contextmenu', event => event.preventDefault());

function controls(): Controls {
  const active = new Set(touches.values());
  return {
    forward: Number(keys.has('KeyW') || keys.has('ArrowUp') || active.has('forward')) - Number(keys.has('KeyS') || keys.has('ArrowDown') || active.has('back')),
    strafe: (Number(keys.has('KeyD') || active.has('right')) - Number(keys.has('KeyA') || active.has('left'))) * (preferences.invert ? -1 : 1),
    turn: Number(keys.has('ArrowRight')) - Number(keys.has('ArrowLeft')),
    sprint: keys.has('ShiftLeft') || keys.has('ShiftRight'), fire: mouseFire || keys.has('Space') || active.has('fire'),
  };
}
function frame(now: number) {
  const dt = Math.min(.05, Math.max(0, (now - lastTime) / 1000)); lastTime = now;
  if (sim) {
    if (playing()) { sim.update(dt, controls()); needsRender = true; }
    for (const event of sim.events.splice(0)) {
      if (started) audio.play(event);
      if (event === 'wave') announce(`VAGUE ${sim.wave} / ${sim.waveLabel.toUpperCase()}`, 2.5);
      if (event === 'boss') announce(`VAGUE ${sim.wave} / SIGNATURE LOURDE DÉTECTÉE`, 3);
      if (event === 'clear') announce('VAGUE TERMINÉE · VIE ET MUNITIONS RÉCUPÉRÉES');
      if (event === 'empty') announce('MUNITIONS ÉPUISÉES · RETOUR AU PISTOLET');
      if (event === 'pickup') announce('RESSOURCES RÉCUPÉRÉES', 1);
    }
    if (started && sim.phase === 'dead') endRun();
    if (started && sim.phase === 'upgrade' && !upgradeDialog.open) { clearInput(); releasePointer(); upgradeDialog.showModal(); }
    if (!paused && !settingsDialog.open && announcementTime > 0) { announcementTime -= dt; if (announcementTime <= 0) text('announcement', ''); }
    hudTimer -= dt; if (hudTimer <= 0) { hud(); hudTimer = .1; }
    if (needsRender) { renderer.render(sim, preferences, started); needsRender = false; }
  }
  requestAnimationFrame(frame);
}
async function boot() {
  syncPreferences(); renderRecords(); void sharedRecords();
  const fallback: Level = { id: 'arena', name: 'Arène', width: 16, height: 16, playerStart: { x: 8.5, y: 8.5 }, data: Array.from({ length: 256 }, (_, i) => i < 16 || i >= 240 || i % 16 === 0 || i % 16 === 15 ? 1 : 0) };
  let degraded = false;
  const assetPromise = renderer.load();
  try {
    const manifestResponse = await fetch('assets/maps/manifest.json', { signal: AbortSignal.timeout(6000) });
    if (!manifestResponse.ok) throw Error('manifest');
    const manifest = await manifestResponse.json();
    if (!Array.isArray(manifest.maps)) throw Error('manifest');
    const results = await Promise.allSettled(manifest.maps.map(async (entry: { file: string }) => {
      if (!/^[a-z0-9_-]+\.json$/.test(entry.file)) throw Error('path');
      const response = await fetch(`assets/maps/${entry.file}`, { signal: AbortSignal.timeout(6000) });
      if (!response.ok) throw Error('map');
      const data: unknown = await response.json(); if (!validLevel(data)) throw Error('invalid map');
      return data;
    }));
    levels = results.flatMap(result => result.status === 'fulfilled' ? [result.value as Level] : []);
    degraded = results.some(result => result.status === 'rejected');
  } catch { degraded = true; }
  if (!levels.length) { levels = [fallback]; degraded = true; }
  levels.sort((a, b) => a.id === 'arena' ? -1 : b.id === 'arena' ? 1 : 0);
  mapSelect.replaceChildren(...levels.map(level => { const option = document.createElement('option'); option.value = level.id; option.textContent = level.name; return option; }));
  previewMap();
  // Slow/failed textures must not prevent the player from starting with fallback rendering.
  get<HTMLButtonElement>('startBtn').disabled = false; text('startBtn', 'Entrer dans le secteur →');
  text('loadStatus', degraded ? 'Certaines cartes sont indisponibles. Les secteurs affichés sont jouables.' : 'Prêt. Cliquez pour jouer et capturer la souris.');
  text('objective', 'CHOISISSEZ VOTRE SECTEUR');
  requestAnimationFrame(frame);
  const failures = await assetPromise; needsRender = true;
  if (failures && !started) text('loadStatus', 'Textures partiellement indisponibles. Le rendu de secours permet de jouer.');
}
void boot().catch(() => { text('loadStatus', 'Impossible de démarrer le moteur. Rechargez la page pour réessayer.'); });
